// frontend/src/components/layout/RightPanel.jsx
import React, { useState, useRef, useEffect, useCallback } from "react"
import { useSelector }                         from "react-redux"
import { selectUser }                          from "../../features/userSlice"
import api                                     from "../../api/axiosInstance"
import DmsPreviewContent                       from "../dms/DmsPreviewContent"
import "./RightPanel.css"

const TABS = [
  { id: "notif",  label: "Alerts",  badge: 4  },
  { id: "chat",   label: "Chat",    badge: 2  },
  { id: "wa",     label: "WA",      badge: 0  },
  { id: "tools",  label: "Tools",   badge: 0  },
  { id: "todo",   label: "Tasks",   badge: 3  },
  { id: "calc",   label: "Calc",    badge: 0  },
  { id: "dms",    label: "DMS",     badge: 0  },   // ← Drop I — DMS preview tab
]

// ── Width presets ─────────────────────────────────────────────
// Default matches the CSS .right-panel { width: 300px } baseline.
// "Wide" preset is for content that benefits from horizontal space
// (PDFs, dashboards). Per-session — does NOT persist across reloads.
const WIDTH_DEFAULT = 300
const WIDTH_MIN     = 280
// Wide is computed at runtime: 60vw, capped at 1200, never less than 600.
const computeWideWidth = () =>
  Math.max(600, Math.min(1200, Math.round(window.innerWidth * 0.60)))
// Max drag width: don't let operator cover the entire viewport.
const computeMaxWidth = () =>
  Math.max(600, window.innerWidth - 200)

const RightPanel = ({ activeTab, onTabChange, onClose, open }) => {
  // ── Width control (per-session, not persisted) ─────────────
  // We start at WIDTH_DEFAULT every time the panel opens. If operator
  // wants wider, they click the expand button or drag the left edge.
  const [width,    setWidth]    = useState(WIDTH_DEFAULT)
  const [expanded, setExpanded] = useState(false)
  const [dragging, setDragging] = useState(false)
  const dragStartRef            = useRef({ x: 0, w: 0 })

  // Reset to default whenever panel reopens (per "no persistence" decision)
  useEffect(() => {
    if (open) {
      setWidth(WIDTH_DEFAULT)
      setExpanded(false)
    }
  }, [open])

  // Toggle between compact (default) and wide preset
  const toggleExpanded = useCallback(() => {
    if (expanded) {
      setWidth(WIDTH_DEFAULT)
      setExpanded(false)
    } else {
      setWidth(computeWideWidth())
      setExpanded(true)
    }
  }, [expanded])

  // Drag-to-resize: mousedown on handle starts; mousemove updates width;
  // mouseup ends. We listen on window, not the handle, because the
  // mouse moves outside the small handle area during a fast drag.
  const startDrag = useCallback((e) => {
    e.preventDefault()
    dragStartRef.current = { x: e.clientX, w: width }
    setDragging(true)
  }, [width])

  useEffect(() => {
    if (!dragging) return
    const onMove = (e) => {
      // Cursor moves LEFT → panel grows (it's docked to the right edge)
      const dx     = dragStartRef.current.x - e.clientX
      const newW   = dragStartRef.current.w + dx
      const maxW   = computeMaxWidth()
      const clamped = Math.max(WIDTH_MIN, Math.min(maxW, newW))
      setWidth(clamped)
      // Keep "expanded" flag in sync — if operator drags to ≥ wide-threshold,
      // mark it expanded so the icon flips to "compact" on the toggle button.
      setExpanded(clamped >= WIDTH_DEFAULT * 1.5)
    }
    const onUp = () => setDragging(false)
    // Disable text selection during drag (prevents annoying highlight feedback)
    const prevUserSelect = document.body.style.userSelect
    document.body.style.userSelect = "none"
    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup",   onUp)
    return () => {
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup",   onUp)
      document.body.style.userSelect = prevUserSelect
    }
  }, [dragging])

  if (!open) return null

  return (
    <aside
      className="right-panel"
      style={{
        width: `${width}px`,
        // Suppress slide-in animation if width is non-default (i.e. mid-resize)
        animation: width !== WIDTH_DEFAULT ? "none" : undefined,
      }}
    >
      {/* Drag handle — thin vertical strip on the left edge */}
      <div
        className={`rp-resize-handle ${dragging ? "dragging" : ""}`}
        onMouseDown={startDrag}
        title="Drag to resize"
      />

      {/* Tab bar */}
      <div className="rp-tabbar">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`rp-tab ${activeTab === t.id ? "active" : ""}`}
            onClick={() => onTabChange(t.id)}
          >
            {t.label}
            {t.badge > 0 && (
              <span className="rp-tab-badge">{t.badge}</span>
            )}
          </button>
        ))}
        <button
          className="rp-expand"
          onClick={toggleExpanded}
          title={expanded ? "Collapse to default width" : "Expand panel"}
        >
          {expanded ? "⤡" : "⤢"}
        </button>
        <button className="rp-close" onClick={onClose} title="Close (Esc)">
          ✕
        </button>
      </div>

      {/* Panel content */}
      <div className="rp-content">
        {activeTab === "notif"  && <NotifPanel />}
        {activeTab === "chat"   && <ChatPanel />}
        {activeTab === "wa"     && <WAPanel />}
        {activeTab === "tools"  && <ToolsPanel />}
        {activeTab === "todo"   && <TodoPanel />}
        {activeTab === "calc"   && <CalcPanel />}
        {activeTab === "dms"    && <DmsPreviewContent />}
      </div>

    </aside>
  )
}

// ── NOTIFICATIONS ─────────────────────────────────────────────
const NOTIFS = [
  { id:1, icon:"⚠", color:"rgba(216,90,48,0.12)", iconColor:"#D85A30",
    text:"SO-0038 placed on credit hold — action required",
    sub:"Sales · just now", unread: true },
  { id:2, icon:"📦", color:"rgba(239,159,39,0.12)", iconColor:"#EF9F27",
    text:"Low stock: Industrial Valve 2 inch (12 units left)",
    sub:"Inventory · 08:30", unread: true },
  { id:3, icon:"✅", color:"rgba(24,95,165,0.12)", iconColor:"#185FA5",
    text:"SO-0041 approved by Admin",
    sub:"Sales · 09:24", unread: true },
  { id:4, icon:"💰", color:"rgba(29,158,117,0.12)", iconColor:"#1D9E75",
    text:"Payment ₹82,000 received from Reliance Corp",
    sub:"Finance · 08:50", unread: true },
  { id:5, icon:"🏛", color:"rgba(239,159,39,0.12)", iconColor:"#EF9F27",
    text:"GSTR-1 for March 2026 due in 5 days",
    sub:"Tax · Today", unread: false },
  { id:6, icon:"📋", color:"rgba(83,74,183,0.12)", iconColor:"#534AB7",
    text:"PO-0018 created — Ashok Suppliers",
    sub:"Purchase · 07:55", unread: false },
]

const NotifPanel = () => {
  const [notifs, setNotifs] = useState(NOTIFS)

  const markAllRead = () => {
    setNotifs(prev => prev.map(n => ({ ...n, unread: false })))
  }

  return (
    <>
      <div className="rp-hdr">
        <span>Notifications</span>
        <button className="rp-hdr-btn" onClick={markAllRead}>
          Mark all read
        </button>
      </div>
      <div className="notif-list">
        {notifs.map(n => (
          <div
            key={n.id}
            className={`notif-item ${n.unread ? "unread" : ""}`}
            onClick={() => setNotifs(prev =>
              prev.map(x => x.id === n.id ? { ...x, unread: false } : x)
            )}
          >
            <div
              className="notif-icon"
              style={{ background: n.color, color: n.iconColor }}
            >
              {n.icon}
            </div>
            <div className="notif-body">
              <div className="notif-text">{n.text}</div>
              <div className="notif-time">{n.sub}</div>
            </div>
            {n.unread && <div className="notif-dot" />}
          </div>
        ))}
      </div>
    </>
  )
}

// ── CHAT ──────────────────────────────────────────────────────
const INIT_MESSAGES = [
  { id:1, from:"Priya S.",  text:"SO-0041 is ready for dispatch",       time:"09:20", out:false },
  { id:2, from:"You",       text:"Thanks! I've approved it",            time:"09:24", out:true  },
  { id:3, from:"Accounts",  text:"Payment ₹82,000 received from Reliance", time:"08:52", out:false },
  { id:4, from:"You",       text:"Please post the receipt voucher",     time:"08:55", out:true  },
  { id:5, from:"Accounts",  text:"Done! RV-0056 posted.",               time:"09:02", out:false },
]

const ChatPanel = () => {
  const [messages,  setMessages]  = useState(INIT_MESSAGES)
  const [input,     setInput]     = useState("")
  const [channel,   setChannel]   = useState("All Team")
  const bottomRef = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  const sendMessage = () => {
    if (!input.trim()) return
    setMessages(prev => [
      ...prev,
      { id: Date.now(), from: "You", text: input.trim(),
        time: new Date().toLocaleTimeString("en-IN", {
          hour:"2-digit", minute:"2-digit"
        }),
        out: true }
    ])
    setInput("")
  }

  return (
    <>
      <div className="rp-hdr">
        <span>Internal Chat</span>
        <select
          className="rp-select"
          value={channel}
          onChange={e => setChannel(e.target.value)}
        >
          {["All Team","Accounts","Sales","Purchase","Logistics"].map(c =>
            <option key={c}>{c}</option>
          )}
        </select>
      </div>

      <div className="chat-messages">
        {messages.map(m => (
          <div key={m.id} className={`chat-bubble ${m.out ? "out" : "in"}`}>
            {!m.out && (
              <div className="chat-sender">{m.from}</div>
            )}
            <div className="chat-text">{m.text}</div>
            <div className="chat-time">{m.time}</div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="chat-input-row">
        <input
          className="chat-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Type a message…"
          onKeyDown={e => e.key === "Enter" && sendMessage()}
        />
        <button className="chat-send" onClick={sendMessage}>➤</button>
      </div>
    </>
  )
}

// ── WHATSAPP ──────────────────────────────────────────────────
const WA_CONTACTS = [
  { name:"Tata Industries",  msg:"Send SO-0041 PDF?",          time:"09:24", unread:2 },
  { name:"Reliance Corp",    msg:"Payment done, please confirm",time:"08:50", unread:1 },
  { name:"Ashok Suppliers",  msg:"PO-0018 received",            time:"Yesterday", unread:0 },
  { name:"Wipro Ltd",        msg:"Need revised quotation",      time:"Yesterday", unread:0 },
  { name:"HCL Technologies", msg:"Invoice copy please",         time:"2 days ago", unread:0 },
]

const WAPanel = () => (
  <>
    <div className="rp-hdr" style={{ background:"#1D9E75", color:"#fff" }}>
      <span>WhatsApp Business</span>
      <span className="wa-status">● Connected</span>
    </div>
    <div className="wa-list">
      {WA_CONTACTS.map(c => (
        <div key={c.name} className="wa-item">
          <div className="wa-avatar">{c.name.charAt(0)}</div>
          <div className="wa-body">
            <div className="wa-name">{c.name}</div>
            <div className="wa-msg">{c.msg}</div>
          </div>
          <div className="wa-meta">
            <div className="wa-time">{c.time}</div>
            {c.unread > 0 && (
              <span className="wa-badge">{c.unread}</span>
            )}
          </div>
        </div>
      ))}
    </div>
    <div style={{ padding:"10px" }}>
      <button
        className="wa-portal-btn"
        onClick={() => window.open("https://web.whatsapp.com","_blank")}
      >
        Open WhatsApp Portal ↗
      </button>
    </div>
  </>
)

// ── TOOLS / INTEGRATIONS ──────────────────────────────────────
const TOOLS = [
  { icon:"📊", name:"GST Portal",     sub:"Verify GSTIN",       url:"https://www.gst.gov.in" },
  { icon:"🏛", name:"MCA21",          sub:"Company search",     url:"https://www.mca.gov.in" },
  { icon:"💹", name:"NSE / BSE",      sub:"Market rates",       url:"https://www.nseindia.com" },
  { icon:"🗺", name:"Google Maps",    sub:"Locate address",     url:"https://maps.google.com" },
  { icon:"📧", name:"Email",          sub:"Compose & send",     action:"email" },
  { icon:"📅", name:"Calendar",       sub:"Schedule meeting",   action:"calendar" },
  { icon:"🔔", name:"SMS / OTP",      sub:"Send alerts",        action:"sms" },
  { icon:"📎", name:"Documents",      sub:"Upload & manage",    action:"docs" },
  { icon:"📤", name:"PDF Export",     sub:"Generate docs",      action:"pdf" },
  { icon:"🔍", name:"Audit Trail",    sub:"Who changed what",   action:"audit" },
]

const ToolsPanel = () => (
  <>
    <div className="rp-hdr"><span>Integrations &amp; Tools</span></div>
    <div className="tools-grid">
      {TOOLS.map(t => (
        <button
          key={t.name}
          className="tool-item"
          onClick={() => t.url
            ? window.open(t.url, "_blank")
            : alert(`${t.name} integration coming soon`)
          }
        >
          <div className="tool-icon">{t.icon}</div>
          <div className="tool-name">{t.name}</div>
          <div className="tool-sub">{t.sub}</div>
        </button>
      ))}
    </div>
  </>
)

// ── TODO / TASKS ──────────────────────────────────────────────
const INIT_TASKS = [
  { id:1, text:"Post receipt voucher — Reliance Corp", priority:"high",   due:"Today",  done:false },
  { id:2, text:"Approve SO-0039 delivery note",        priority:"high",   due:"Today",  done:false },
  { id:3, text:"Send revised quote to Wipro Ltd",      priority:"medium", due:"Tomorrow",done:false },
  { id:4, text:"Review GSTR-1 for March 2026",         priority:"high",   due:"15 Apr", done:false },
  { id:5, text:"Physical stock count — Warehouse A",   priority:"medium", due:"20 Apr", done:false },
  { id:6, text:"Process payroll for April",            priority:"low",    due:"30 Apr", done:false },
]

const TodoPanel = () => {
  const [tasks,    setTasks]    = useState(INIT_TASKS)
  const [newTask,  setNewTask]  = useState("")
  const [showAdd,  setShowAdd]  = useState(false)

  const toggleDone = (id) => {
    setTasks(prev =>
      prev.map(t => t.id === id ? { ...t, done: !t.done } : t)
    )
  }

  const addTask = () => {
    if (!newTask.trim()) return
    setTasks(prev => [...prev, {
      id:       Date.now(),
      text:     newTask.trim(),
      priority: "medium",
      due:      "Today",
      done:     false,
    }])
    setNewTask("")
    setShowAdd(false)
  }

  return (
    <>
      <div className="rp-hdr">
        <span>My Tasks</span>
        <button
          className="rp-hdr-btn"
          onClick={() => setShowAdd(p => !p)}
        >
          + Add
        </button>
      </div>

      {showAdd && (
        <div className="todo-add-row">
          <input
            className="todo-add-input"
            placeholder="Task description…"
            value={newTask}
            onChange={e => setNewTask(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addTask()}
            autoFocus
          />
          <button className="todo-add-btn" onClick={addTask}>Add</button>
        </div>
      )}

      <div className="todo-list">
        {tasks.map(t => (
          <div
            key={t.id}
            className={`todo-item ${t.done ? "done" : ""}`}
          >
            <input
              type="checkbox"
              checked={t.done}
              onChange={() => toggleDone(t.id)}
              className="todo-check"
            />
            <div className="todo-body">
              <div className="todo-text">{t.text}</div>
              <div className="todo-meta">{t.due}</div>
            </div>
            <span className={`todo-priority p-${t.priority}`}>
              {t.priority}
            </span>
          </div>
        ))}
      </div>
    </>
  )
}

// ── CALCULATOR ────────────────────────────────────────────────
const CALC_KEYS = [
  ["C", "±", "%", "÷"],
  ["7", "8", "9", "×"],
  ["4", "5", "6", "−"],
  ["1", "2", "3", "+"],
  ["0", ".",  "=", "="],
]

const CalcPanel = () => {
  const [expr,    setExpr]    = useState("")
  const [display, setDisplay] = useState("0")
  const [history, setHistory] = useState([])

  const handleKey = (k) => {
    if (k === "C") {
      setExpr("")
      setDisplay("0")
      return
    }
    if (k === "±") {
      setExpr(prev => prev ? `-(${prev})` : "0")
      return
    }
    if (k === "%") {
      try {
        const v = Function(`"use strict";return(${expr})`)()
        const r = v / 100
        setDisplay(String(r))
        setExpr(String(r))
      } catch { /* ignore */ }
      return
    }
    if (k === "=") {
      try {
        const safe = expr
          .replace(/÷/g, "/")
          .replace(/×/g, "*")
          .replace(/−/g, "-")
        const result = Function(`"use strict";return(${safe})`)()
        const rounded = parseFloat(result.toFixed(6))
        setHistory(prev => [
          { expr, result: rounded },
          ...prev.slice(0, 4)
        ])
        setDisplay(rounded.toLocaleString("en-IN"))
        setExpr(String(rounded))
      } catch {
        setDisplay("Error")
        setExpr("")
      }
      return
    }

    const newExpr = expr + k
    setExpr(newExpr)
    setDisplay(newExpr)
  }

  const pasteToField = () => {
    // Paste to focused input if any
    const active = document.activeElement
    if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA")) {
      const nativeInput = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype, "value"
      )
      nativeInput.set.call(active, display.replace(/,/g, ""))
      active.dispatchEvent(new Event("input", { bubbles: true }))
    } else {
      navigator.clipboard?.writeText(display.replace(/,/g, ""))
        .catch(() => {})
    }
  }

  return (
    <>
      <div className="rp-hdr"><span>Calculator</span></div>

      <div className="calc-display">
        <div className="calc-expr">{expr || "0"}</div>
        <div className="calc-result">{display}</div>
      </div>

      <div className="calc-grid">
        {CALC_KEYS.flat().map((k, i) => (
          <button
            key={i}
            className={`calc-key ${
              k === "=" ? "key-eq" :
              ["÷","×","−","+","%","±"].includes(k) ? "key-op" :
              k === "C" ? "key-clear" : "key-num"
            }`}
            onClick={() => handleKey(k)}
          >
            {k}
          </button>
        ))}
      </div>

      <button className="calc-paste" onClick={pasteToField}>
        ↑ Paste to active field
      </button>

      {history.length > 0 && (
        <div className="calc-history">
          {history.map((h, i) => (
            <div key={i} className="calc-hist-item">
              <span className="calc-hist-expr">{h.expr}</span>
              <span className="calc-hist-result">
                = {h.result.toLocaleString("en-IN")}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

export default RightPanel
